import { Button, ErrorCard } from '@starsoft/common/components';
import {
  CreateDepositRequestDto,
  CreateKycRequestDto,
} from '@starsoft/common/dto';
import {
  useKycFormSubmission,
  useKycMeByOriginIdWithOrigin,
  useKycNavigation,
  useTranslation,
} from '@starsoft/common/hooks';
import { PropsWithChildren, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useCoallescedCoinId } from '../../../../../../hooks/coin/useCoallescedCoinId';
import KycUserIdentityNoKycToSelectedCoin from '../../NoKycToSelectedCoin';
import { UserIdentityFormRendererLayoutProps } from './props';
import styles from './styles.module.scss';

/**
 * UserIdentityFormRendererLayout Component
 * Renders a form layout for KYC (Know Your Customer) identity verification.
 * Handles form state, submission, and different view states including loading and errors.
 *
 * @component
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components to render within the form layout
 * @param {boolean} props.isContainerColor - Whether to use container background color for skeleton
 * @returns {JSX.Element} The rendered form layout with appropriate state handling
 *
 * @example
 * // Basic usage with container color
 * <UserIdentityFormRendererLayout isContainerColor={true}>
 *   <FormContent />
 * </UserIdentityFormRendererLayout>
 *
 * // Without container color
 * <UserIdentityFormRendererLayout isContainerColor={false}>
 *   <FormFields />
 * </UserIdentityFormRendererLayout>
 */
export default function UserIdentityFormRendererLayout({
  children,
  isContainerColor,
}: PropsWithChildren<UserIdentityFormRendererLayoutProps>) {
  const { t } = useTranslation('common');
  const { coinId } = useCoallescedCoinId();
  const methods = useForm<CreateDepositRequestDto & CreateKycRequestDto>();
  const { isPending, error, kyc, refetch } = useKycMeByOriginIdWithOrigin();
  const { handleSubmit, reset } = methods;
  const { handleKycSubmission, isLoadingKycSubmission } = useKycFormSubmission({
    coinId: coinId?.toString(),
    kyc,
    methods,
  });

  const { hasKycCheckInCurrentIdentity } = useKycNavigation({
    coinId: coinId?.toString(),
  });

  /**
   * Resets the form with KYC data when available
   * Populates form fields with existing identity information
   */
  function handleResetForm() {
    if (!kyc) {
      return;
    }

    reset({
      identity: kyc?.identity,
      identityId: kyc?.identity?.id?.toString(),
    });
  }

  useEffect(handleResetForm, [kyc, reset]);

  // Show error card if there's an error
  if (error) {
    return <ErrorCard error={error} refetch={refetch} />;
  }

  // Show loading state with skeleton button
  if (isPending) {
    return (
      <FormProvider {...methods}>
        <div className={styles.container}>
          <div className={styles.container__content}>{children}</div>
          <Button isSkeleton skeletonDefault={isContainerColor} />
        </div>
      </FormProvider>
    );
  }

  // Show no KYC required message if no check is needed
  if (!hasKycCheckInCurrentIdentity) {
    return <KycUserIdentityNoKycToSelectedCoin />;
  }

  // Render the main form with submit button if no existing KYC
  return (
    <FormProvider {...methods}>
      <form
        id="kyc-form"
        className={styles.container}
        onSubmit={handleSubmit(handleKycSubmission)}
      >
        <div className={styles.container__content}>{children}</div>
        {!kyc && (
          <Button
            form="kyc-form"
            type="submit"
            isloading={isLoadingKycSubmission}
          >
            {t('create_kyc')}
          </Button>
        )}
      </form>
    </FormProvider>
  );
}
